/**
 * AP-PAY-01: Finance Payment Workspace
 * Route: /ap/payments (ap.invoice.view + ap.payment.record)
 *
 * Lists all supplier payments with status filters.
 * Finance can: Record Payment, Confirm, Allocate, Reverse, Create Credit Note.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, RefreshCw, Search, Filter, Check, ChevronsUpDown,
  AlertTriangle, CheckCircle2, Clock, XCircle, RotateCcw,
  DollarSign, FileText, ChevronRight, Building2, Calendar,
  CreditCard, Banknote,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency,
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(minor / 100);
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

const STATUS_CFG = {
  INITIATED:            { label: "Initiated",           bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE", Icon: Clock },
  PENDING_CONFIRMATION: { label: "Pending Confirmation", bg: "#FEF3C7", text: "#92400E", border: "#FDE68A", Icon: Clock },
  CONFIRMED:            { label: "Confirmed",            bg: "#F0FDF4", text: "#166534", border: "#BBF7D0", Icon: CheckCircle2 },
  REVERSED:             { label: "Reversed",             bg: "#FEF2F2", text: "#991B1B", border: "#FECACA", Icon: XCircle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || { label: status, bg: "#F5F5F5", text: "#666", border: "#DDD", Icon: Clock };
  const Ic = cfg.Icon;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "4px",
      padding: "3px 10px", borderRadius: "20px",
      fontSize: "11px", fontWeight: 700,
      background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}`,
    }}>
      <Ic size={10} /> {cfg.label}
    </span>
  );
}

const ALL_STATUSES = ["INITIATED", "PENDING_CONFIRMATION", "CONFIRMED", "REVERSED"];

// ── Main Component ─────────────────────────────────────────────────────────────

export default function FinancePaymentWorkspace() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [items,       setItems]       = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [search,      setSearch]      = useState("");

  // Record Payment modal
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [suppliers,       setSuppliers]       = useState([]);
  const [legalEntities,   setLegalEntities]   = useState([]);
  const [recordForm, setRecordForm] = useState({
    supplier_id: "", buyer_legal_entity_id: "",
    currency_code: "INR", payment_amount_minor: "",
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: "BANK_TRANSFER", external_transaction_ref: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const PAGE_SIZE = 20;
  const hasCap    = (cap) => user?.capabilities?.includes(cap) || user?.capabilities?.includes("ap.*");
  const canRecord = hasCap("ap.payment.record");

  const loadPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (activeFilter !== "ALL") params.status = activeFilter;
      const res = await axios.get(`${API}/api/ap/payments`, { params, withCredentials: true });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load payments");
    } finally {
      setLoading(false);
    }
  }, [page, activeFilter]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  const loadSuppliers = useCallback(async () => {
    if (!showRecordModal) return;
    try {
      const [supRes, leRes] = await Promise.all([
        axios.get(`${API}/api/sourcing/suppliers`, { withCredentials: true }),
        axios.get(`${API}/api/organization/legal-entities`, { withCredentials: true }),
      ]);
      setSuppliers(supRes.data?.items || supRes.data || []);
      const leData = leRes.data;
      setLegalEntities(Array.isArray(leData) ? leData : (leData?.items || []));
    } catch {
      // non-blocking — supplier list may come from different endpoint
    }
  }, [showRecordModal]);

  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);

  const submitRecord = async (e) => {
    e.preventDefault();
    if (!recordForm.supplier_id)           return toast.error("Supplier is required");
    if (!recordForm.buyer_legal_entity_id) return toast.error("Legal Entity is required");
    const amtMinor = parseInt(recordForm.payment_amount_minor, 10);
    if (!amtMinor || amtMinor <= 0)        return toast.error("Amount must be a positive integer (minor units)");

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/ap/payments`, {
        ...recordForm,
        payment_amount_minor: amtMinor,
      }, { withCredentials: true });
      toast.success("Payment recorded successfully");
      setShowRecordModal(false);
      setRecordForm({
        supplier_id: "", buyer_legal_entity_id: "",
        currency_code: "INR", payment_amount_minor: "",
        payment_date: new Date().toISOString().slice(0, 10),
        payment_method: "BANK_TRANSFER", external_transaction_ref: "", notes: "",
      });
      loadPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record payment");
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div data-testid="finance-payment-workspace" style={{ minHeight: "100vh", background: "#F8F7F4" }}>

      {/* ── Sticky Header ─────────────────────────────────────────────────── */}
      <div style={{
        position: "sticky", top: 0, zIndex: 30,
        background: "rgba(248,247,244,0.96)", backdropFilter: "blur(12px)",
        borderBottom: "1px solid #E7D8C0",
        padding: "16px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px",
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#1C160C", letterSpacing: "-0.5px" }}>
            Supplier Payment Workspace
          </h1>
          <p style={{ margin: 0, fontSize: "12px", color: "#9E8A6B" }}>
            AP-PAY-01 · Record, confirm and allocate supplier payments
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button data-testid="refresh-payments-btn"
            onClick={loadPayments}
            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px",
              background: "transparent", border: "1px solid #D4C5A9", borderRadius: "8px",
              fontSize: "13px", color: "#6B5B4A", cursor: "pointer" }}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          {canRecord && (
            <button data-testid="record-payment-btn"
              onClick={() => setShowRecordModal(true)}
              style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 16px",
                background: "#1C160C", border: "none", borderRadius: "8px",
                fontSize: "13px", fontWeight: 600, color: "#FFF", cursor: "pointer" }}>
              <Plus size={14} /> Record Payment
            </button>
          )}
          <button
            data-testid="nav-credit-notes-btn"
            onClick={() => navigate("/ap/credit-notes")}
            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px",
              background: "transparent", border: "1px solid #D4C5A9", borderRadius: "8px",
              fontSize: "13px", color: "#6B5B4A", cursor: "pointer" }}>
            <FileText size={14} /> Credit Notes
          </button>
        </div>
      </div>

      <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>

        {/* ── Status Filter Tabs ──────────────────────────────────────────── */}
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "20px" }}>
          {["ALL", ...ALL_STATUSES].map((st) => (
            <button
              key={st}
              data-testid={`filter-${st}`}
              onClick={() => { setActiveFilter(st); setPage(1); }}
              style={{
                padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: 600,
                cursor: "pointer", transition: "all 0.15s",
                background: activeFilter === st ? "#1C160C" : "transparent",
                color: activeFilter === st ? "#FFF" : "#6B5B4A",
                border: activeFilter === st ? "none" : "1px solid #D4C5A9",
              }}>
              {st.replace("_", " ")}
            </button>
          ))}
          <span style={{ marginLeft: "auto", fontSize: "12px", color: "#9E8A6B", alignSelf: "center" }}>
            {total} payment{total !== 1 ? "s" : ""}
          </span>
        </div>

        {/* ── Payment Table ───────────────────────────────────────────────── */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#9E8A6B", fontSize: "14px" }}>
            Loading payments…
          </div>
        ) : items.length === 0 ? (
          <div data-testid="payment-empty-state" style={{
            textAlign: "center", padding: "80px 0",
            background: "#FFF", borderRadius: "16px", border: "1px solid #E7D8C0",
          }}>
            <Banknote size={40} style={{ color: "#D4C5A9", marginBottom: "12px" }} />
            <p style={{ color: "#9E8A6B", fontSize: "15px", margin: 0 }}>
              {activeFilter === "ALL" ? "No payments recorded yet" : `No ${activeFilter.replace("_", " ").toLowerCase()} payments`}
            </p>
            {canRecord && (
              <button onClick={() => setShowRecordModal(true)} style={{
                marginTop: "16px", padding: "8px 20px", background: "#1C160C",
                color: "#FFF", border: "none", borderRadius: "8px", fontSize: "13px",
                fontWeight: 600, cursor: "pointer",
              }}>
                Record First Payment
              </button>
            )}
          </div>
        ) : (
          <div style={{ background: "#FFF", borderRadius: "16px", border: "1px solid #E7D8C0", overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ background: "#FDFAF5", borderBottom: "1px solid #E7D8C0" }}>
                    {["AP Reference", "Supplier", "Date", "Method", "Amount", "Confirmed", "Allocated", "Status", ""].map((h) => (
                      <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, color: "#9E8A6B", fontSize: "11px", whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((pay, idx) => {
                    const unalloc = pay.unallocated_amount_minor ?? (pay.confirmed_amount_minor ?? 0);
                    return (
                      <tr key={pay.id} data-testid={`payment-row-${idx}`}
                        style={{ borderBottom: "1px solid #F3EDE3", transition: "background 0.1s" }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#FDFAF5"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <td style={{ padding: "14px 16px" }}>
                          <span style={{ fontWeight: 700, color: "#1C160C", fontFamily: "monospace", fontSize: "12px" }}>
                            {pay.ap_payment_reference || pay.id?.slice(-8)}
                          </span>
                          {pay.external_transaction_ref && (
                            <div style={{ fontSize: "11px", color: "#9E8A6B" }}>
                              UTR: {pay.external_transaction_ref}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "14px 16px", color: "#3A2E1E" }}>
                          {pay.supplier_name || pay.supplier_id}
                        </td>
                        <td style={{ padding: "14px 16px", color: "#9E8A6B", whiteSpace: "nowrap" }}>
                          {fmtDate(pay.payment_date)}
                        </td>
                        <td style={{ padding: "14px 16px", color: "#9E8A6B", fontSize: "12px" }}>
                          {pay.payment_method?.replace("_", " ")}
                        </td>
                        <td style={{ padding: "14px 16px", color: "#1C160C", fontWeight: 600 }}>
                          {fmt(pay.payment_amount_minor, pay.currency_code)}
                        </td>
                        <td style={{ padding: "14px 16px", color: pay.confirmed_amount_minor ? "#166534" : "#9E8A6B" }}>
                          {pay.confirmed_amount_minor ? fmt(pay.confirmed_amount_minor, pay.currency_code) : "—"}
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          {pay.allocated_amount_minor > 0 ? (
                            <span style={{ color: "#166534", fontWeight: 600 }}>
                              {fmt(pay.allocated_amount_minor, pay.currency_code)}
                            </span>
                          ) : <span style={{ color: "#9E8A6B" }}>—</span>}
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <StatusBadge status={pay.status} />
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <button
                            data-testid={`view-payment-btn-${idx}`}
                            onClick={() => navigate(`/ap/payments/${pay.id}`)}
                            style={{ display: "flex", alignItems: "center", gap: "4px", color: "#9E8A6B",
                              background: "transparent", border: "none", cursor: "pointer", fontSize: "12px" }}>
                            View <ChevronRight size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ padding: "12px 16px", borderTop: "1px solid #E7D8C0",
                display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "12px", color: "#9E8A6B" }}>
                  Page {page} of {totalPages}
                </span>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #D4C5A9",
                      background: "transparent", cursor: page === 1 ? "default" : "pointer",
                      color: page === 1 ? "#D4C5A9" : "#6B5B4A", fontSize: "12px" }}>
                    Prev
                  </button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #D4C5A9",
                      background: "transparent", cursor: page === totalPages ? "default" : "pointer",
                      color: page === totalPages ? "#D4C5A9" : "#6B5B4A", fontSize: "12px" }}>
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          Record Payment Modal
      ══════════════════════════════════════════════════════════════════ */}
      {showRecordModal && (
        <div data-testid="record-payment-modal"
          style={{
            position: "fixed", inset: 0, zIndex: 50,
            background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowRecordModal(false); }}
        >
          <div style={{
            background: "#FFF", borderRadius: "20px", width: "100%", maxWidth: "560px",
            padding: "32px", boxShadow: "0 24px 80px rgba(0,0,0,0.2)",
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: "0 0 6px", fontSize: "18px", fontWeight: 700, color: "#1C160C" }}>
              Record Supplier Payment
            </h2>
            <p style={{ margin: "0 0 24px", fontSize: "12px", color: "#9E8A6B" }}>
              AP-PAY-01B · This records a payment already made outside the platform.
              No automatic bank transfer is initiated.
            </p>

            <form onSubmit={submitRecord} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Supplier */}
              <div>
                <label style={labelStyle}>Supplier <span style={{ color: "#BA0517" }}>*</span></label>
                {suppliers.length > 0 ? (
                  <select
                    data-testid="record-supplier-select"
                    value={recordForm.supplier_id}
                    onChange={(e) => setRecordForm(f => ({ ...f, supplier_id: e.target.value }))}
                    style={inputStyle} required
                  >
                    <option value="">Select supplier…</option>
                    {suppliers.map((s) => (
                      <option key={s.id || s._id} value={s.id || s._id}>
                        {s.company_name || s.display_name || s.id}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    data-testid="record-supplier-id-input"
                    value={recordForm.supplier_id}
                    onChange={(e) => setRecordForm(f => ({ ...f, supplier_id: e.target.value }))}
                    placeholder="Supplier ID"
                    style={inputStyle} required
                  />
                )}
              </div>

              {/* Legal Entity */}
              <div>
                <label style={labelStyle}>Buyer Legal Entity <span style={{ color: "#BA0517" }}>*</span></label>
                {legalEntities.length > 0 ? (
                  <select
                    data-testid="record-le-select"
                    value={recordForm.buyer_legal_entity_id}
                    onChange={(e) => setRecordForm(f => ({ ...f, buyer_legal_entity_id: e.target.value }))}
                    style={inputStyle} required
                  >
                    <option value="">Select legal entity…</option>
                    {legalEntities.map((le) => (
                      <option key={le.id || le._id} value={le.id || le._id}>
                        {le.name || le.id}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    data-testid="record-le-id-input"
                    value={recordForm.buyer_legal_entity_id}
                    onChange={(e) => setRecordForm(f => ({ ...f, buyer_legal_entity_id: e.target.value }))}
                    placeholder="Legal Entity ID"
                    style={inputStyle} required
                  />
                )}
              </div>

              {/* Amount + Currency row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 100px", gap: "12px" }}>
                <div>
                  <label style={labelStyle}>Amount (minor units) <span style={{ color: "#BA0517" }}>*</span></label>
                  <input
                    data-testid="record-amount-input"
                    type="number" min="1" step="1"
                    value={recordForm.payment_amount_minor}
                    onChange={(e) => setRecordForm(f => ({ ...f, payment_amount_minor: e.target.value }))}
                    placeholder="e.g. 1000000 = ₹10,000"
                    style={inputStyle} required
                  />
                </div>
                <div>
                  <label style={labelStyle}>Currency</label>
                  <select
                    data-testid="record-currency-select"
                    value={recordForm.currency_code}
                    onChange={(e) => setRecordForm(f => ({ ...f, currency_code: e.target.value }))}
                    style={inputStyle}
                  >
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>

              {/* Date + Method row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={labelStyle}>Payment Date <span style={{ color: "#BA0517" }}>*</span></label>
                  <input
                    data-testid="record-date-input"
                    type="date"
                    value={recordForm.payment_date}
                    onChange={(e) => setRecordForm(f => ({ ...f, payment_date: e.target.value }))}
                    style={inputStyle} required
                  />
                </div>
                <div>
                  <label style={labelStyle}>Payment Method</label>
                  <select
                    data-testid="record-method-select"
                    value={recordForm.payment_method}
                    onChange={(e) => setRecordForm(f => ({ ...f, payment_method: e.target.value }))}
                    style={inputStyle}
                  >
                    {["BANK_TRANSFER","NEFT","RTGS","IMPS","CHEQUE","ONLINE","OTHER"].map(m => (
                      <option key={m} value={m}>{m.replace("_"," ")}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* UTR / External Ref */}
              <div>
                <label style={labelStyle}>Bank UTR / Transaction Reference</label>
                <input
                  data-testid="record-utr-input"
                  value={recordForm.external_transaction_ref}
                  onChange={(e) => setRecordForm(f => ({ ...f, external_transaction_ref: e.target.value }))}
                  placeholder="Optional — bank UTR or external reference"
                  style={inputStyle}
                />
              </div>

              {/* Notes */}
              <div>
                <label style={labelStyle}>Notes</label>
                <textarea
                  data-testid="record-notes-input"
                  value={recordForm.notes}
                  onChange={(e) => setRecordForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Optional payment notes"
                  rows={2}
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </div>

              <div style={{ padding: "12px 0 0", borderTop: "1px solid #F3EDE3",
                display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button type="button" onClick={() => setShowRecordModal(false)}
                  data-testid="record-cancel-btn"
                  style={{ padding: "10px 20px", borderRadius: "8px", border: "1px solid #D4C5A9",
                    background: "transparent", color: "#6B5B4A", cursor: "pointer", fontSize: "13px" }}>
                  Cancel
                </button>
                <button type="submit" disabled={submitting}
                  data-testid="record-submit-btn"
                  style={{ padding: "10px 22px", borderRadius: "8px", border: "none",
                    background: submitting ? "#9E8A6B" : "#1C160C", color: "#FFF",
                    fontWeight: 600, fontSize: "13px", cursor: submitting ? "default" : "pointer" }}>
                  {submitting ? "Recording…" : "Record Payment"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const labelStyle = {
  display: "block", marginBottom: "5px",
  fontSize: "12px", fontWeight: 600, color: "#6B5B4A",
};
const inputStyle = {
  width: "100%", padding: "9px 12px", boxSizing: "border-box",
  border: "1px solid #D4C5A9", borderRadius: "8px",
  fontSize: "13px", color: "#1C160C", background: "#FDFAF5",
  outline: "none",
};
